import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { css } from "@emotion/react";
import { PrimaryLink } from "components/Links";
import Icon, { withIcon, ICON_TYPE, ICON_SIZE } from "components/Icon";
import { colors, fontSize } from "style";
import { shortenText } from "utils";

/**
 * FileUpload
 *
 * @param {Object}     value
 * @param {Function}   onChange
 * @param {String}     name
 * @param {String}     accept - File types to accept (default: "application/pdf")
 * @param {String}     uploadMessageId - Translation ID for upload button text
 * @param {Boolean}    disabled
 * @param {Boolean}    showFilePreview - Whether to show file preview after selection (default: true)
 * @param {Boolean}    hideTrigger - Whether to hide the upload trigger (default: false)
 */
const FileUpload = forwardRef(
  (
    {
      value,
      onChange,
      name,
      accept = "application/pdf",
      uploadMessageId,
      disabled = false,
      showFilePreview = true,
      hideTrigger = false,
      ...props
    },
    ref
  ) => {
    const [fileStatus, setFileStatus] = useState(Boolean(value) && showFilePreview);
    const fileInput = useRef(null);

    useImperativeHandle(ref, () => ({
      openFileDialog: () => {
        if (disabled) return;
        fileInput.current?.click();
      },
    }));

    const handleFileSelect = ({
      target: {
        validity,
        files: [file],
      },
    }) => {
      if (disabled) return;

      if (validity.valid) {
        onChange(name, file);
        setFileStatus(showFilePreview);
      }
    };

    const handleUploadClick = () => {
      if (disabled) return;
      fileInput.current?.click();
    };

    return (
      <>
        {!hideTrigger && (
          <div css={styles.container(disabled)}>
            {fileStatus && (
              <div css={styles.file_container}>
                <span css={styles.file_preview(fileStatus, disabled)}>
                  <Icon type={ICON_TYPE.file} size={ICON_SIZE.xxxlarge} color="inherit" />
                </span>

                <div css={styles.success_container}>
                  <Icon type={ICON_TYPE.checkFilled} size={ICON_SIZE.xxlarge} color={colors.green} />
                </div>

                {value?.name && <span css={styles.success_indicator}>{shortenText(value.name, 20, false, true)}</span>}
              </div>
            )}

            <div css={styles.link_container}>
              <LinkUploadFileWithIcon onClick={handleUploadClick} css={styles.link(disabled)}>
                <FormattedMessage id={uploadMessageId || "Global.UploadFile"} />
              </LinkUploadFileWithIcon>
            </div>
          </div>
        )}

        <div css={styles.input}>
          <input
            ref={fileInput}
            type="file"
            accept={accept}
            onChange={handleFileSelect}
            disabled={disabled}
            {...props}
          />
        </div>
      </>
    );
  }
);

const LinkUploadFileWithIcon = withIcon(PrimaryLink, {
  type: ICON_TYPE.upload,
  size: ICON_SIZE.xlarge,
  color: "inherit",
  position: "relative",
});

FileUpload.displayName = "FileUpload";

const styles = {
  container: (disabled) => css`
    display: flex;
    align-items: center;
    font-size: ${fontSize.small};
    font-weight: 600;
    opacity: ${disabled ? 0.5 : 1};
    pointer-events: ${disabled ? "none" : "auto"};
  `,
  link: (disabled) => css`
    cursor: ${disabled ? "not-allowed" : "pointer"};
  `,
  success_container: css`
    position: absolute;
    bottom: 53%;
    left: 50%;
    display: flex;
    align-items: center;
  `,
  success_indicator: css`
    color: ${colors.grayAnatomyLight1};
    position: absolute;
    bottom: 1rem;
    left: 0;
    width: 100%;
    text-align: center;
    font-size: ${fontSize.xxsmall};
    font-weight: 400;
  `,
  file_container: css`
    width: 18rem;
    height: 18rem;
    position: relative;
    margin-right: 2rem;
  `,
  file_preview: (fileUploaded, disabled) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: ${colors.grayAnatomyLight5};
    width: 18rem;
    height: 18rem;
    border-radius: 0.6rem;
    color: ${fileUploaded ? colors.green : colors.grayAnatomyLight3};

    ${!fileUploaded &&
    `
      border: 2px dashed ${colors.grayAnatomyLight2};
    `}

    ${!disabled &&
    `
      &:hover {
        background: ${colors.grayAnatomyLight4};
        color: ${colors.purpleRainBase};
      }
    `}
  `,
  input: css`
    display: none;
  `,
  link_container: css`
    margin-right: 2rem;
  `,
};

FileUpload.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  name: PropTypes.string,
  accept: PropTypes.string,
  uploadMessageId: PropTypes.string,
  disabled: PropTypes.bool,
  showFilePreview: PropTypes.bool,
  hideTrigger: PropTypes.bool,
};

export default FileUpload;
